/**
 * PATH: packages/ui/src/kanban/hooks/shouldDismissContextMenu.ts
 * FILE_ID: UI-KANBAN-HOOK-DISMISS-001
 * PURPOSE: Pure I→O guard for "did this pointer event happen outside the menu?"
 * WHY: Bubble-phase `document.mousedown` misses targets when a child calls
 *      `stopPropagation()` (Gantt drag handles, card chrome, etc.). Capture-
 *      phase listeners still run first, but a full-screen backdrop is the
 *      most reliable dismiss surface — this helper backs both paths.
 * FLOW:
 *   ┌──────────────┐    ┌─────────────────────┐    ┌────────────┐
 *   │ menuRoot ref │ →  │ contains(target)?   │ →  │ dismiss?   │
 *   └──────────────┘    └─────────────────────┘    └────────────┘
 */

/** ARC-6: nested popovers (SmartDateInput's calendar) portal to <body>, so a
 *  raw contains() check reads them as "outside" the host menu and a click on
 *  a calendar day would dismiss the menu mid-interaction. Any element inside
 *  a [data-kanban-nested-popover], [data-quotech-menu-nested], or
 *  [data-kanban-overlay] subtree counts as INSIDE its host.
 *  Invariant: interacting with a nested kanban popover or the create-board
 *  dialog must never dismiss the menu that opened it. */
const NESTED_POPOVER_SELECTOR = '[data-kanban-nested-popover], [data-quotech-menu-nested], [data-kanban-overlay]'

export function shouldDismissContextMenu(
  menuRoot: HTMLElement | null,
  target: EventTarget | null,
): boolean {
  if (!menuRoot) return false
  if (!(target instanceof Node)) return true
  if (menuRoot.contains(target)) return false
  const el = target instanceof Element ? target : target.parentElement
  if (el?.closest(NESTED_POPOVER_SELECTOR)) return false
  return true
}
